import { useEffect, useState } from 'react';
import { Outlet, useParams, useNavigate } from 'react-router-dom';
import ManageSchoolSidebar from './ManageSchoolSidebar';
import { ShieldAlert } from 'lucide-react';

export default function ManageSchoolLayout() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);

  useEffect(() => {
    // Get user data from localStorage
    const userStr = localStorage.getItem('crm_user');
    const user = userStr ? JSON.parse(userStr) : null;

    // Get client_id and sd_org_id from user data (support multiple data structures)
    const clientId = user?.data?.data?.client_id_fk || user?.data?.client_id_fk || user?.client_id_fk;
    const userSdOrgId = user?.data?.data?.sd_org_id || user?.data?.data?.client?.sd_org_id || user?.data?.sd_org_id || user?.sd_org_id || user?.client?.sd_org_id;

    // Client ID 9 can access any school
    if (clientId == 9 || clientId === '9') {
      setIsAuthorized(true);
      return;
    }

    // For school admins, verify the URL school ID matches their sd_org_id
    if (userSdOrgId && id) {
      const urlSchoolId = parseInt(id, 10);
      const userSchoolId = typeof userSdOrgId === 'string' ? parseInt(userSdOrgId, 10) : userSdOrgId;

      if (urlSchoolId === userSchoolId) {
        setIsAuthorized(true);
      } else {
        setIsAuthorized(false);
      }
    } else {
      // No sd_org_id means user shouldn't access manage school at all
      setIsAuthorized(false);
    }
  }, [id]);

  // Loading state
  if (isAuthorized === null) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  // Unauthorized access
  if (!isAuthorized) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center p-8 max-w-md">
          <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
            <ShieldAlert className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You don't have permission to access this school. You can only manage your own school.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brandColor/90 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-gray-50 dark:bg-gray-900">
      <ManageSchoolSidebar />
      <div className="flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
}
